/**
 * HotspotManager - Reusable hotspot/interactive image component
 * Used across home, bridges, electronics, and pickup-modules pages
 */
class HotspotManager {
    __(s) {
        return typeof window.__ === 'function' ? window.__(s) : s;
    }

    constructor(config) {
        this.containerId = config.containerId;
        this.parts = config.parts;
        this.imageContainerId = config.imageContainerId;
        this.preloadImageId = config.preloadImageId;

        this.options = {
            hasLearnMore: config.hasLearnMore || false,
            hasOverlay: config.hasOverlay !== false,
            hasDescriptionPanel: config.hasDescriptionPanel || false,
            descriptionPanelSelector: config.descriptionPanelSelector || null,
            mobileBreakpoint: config.mobileBreakpoint || 768,
            tabletBreakpoint: config.tabletBreakpoint || 1024,
            useEmUnits: config.useEmUnits || false,
            defaultCircleSize: config.defaultCircleSize || { mobile: 50, tablet: 75, desktop: 100 },
            onHotspotClick: config.onHotspotClick || null,
            containerTemplate: config.containerTemplate || null,
            ...config.options
        };

        this.imageLoaded = false;
        this.deviceType = this.getDeviceType();
        this.currentHotspotIndex = -1;
        this.activeHotspot = null;
        this.activeHotspotContainer = null;
        this.activeHotspotContainerClass = null;
        this.resizeTimer = null;
    }

    getDeviceType() {
        const width = window.innerWidth;
        if (width <= this.options.mobileBreakpoint) return 'mobile';
        if (width <= this.options.tabletBreakpoint) return 'tablet';
        return 'desktop';
    }

    isMobile() {
        return window.innerWidth <= this.options.mobileBreakpoint;
    }

    isTablet() {
        const width = window.innerWidth;
        return width > this.options.mobileBreakpoint && width <= this.options.tabletBreakpoint;
    }

    isDesktop() {
        return window.innerWidth > this.options.tabletBreakpoint;
    }

    getCoordinates(part, axis) {
        const prop = axis.toLowerCase();
        const mobileProp = `mobile${axis}`;
        const tabletProp = `tablet${axis}`;

        switch (this.deviceType) {
            case 'mobile':
                return part[mobileProp] !== undefined ? part[mobileProp] : part[prop];
            case 'tablet':
                return part[tabletProp] !== undefined ? part[tabletProp] : part[prop];
            default:
                return part[prop];
        }
    }

    getLabelOffset(part, axis) {
        const baseProp = `labelOffset${axis}`;
        const mobileProp = `mobileLabelOffset${axis}`;
        const tabletProp = `tabletLabelOffset${axis}`;

        switch (this.deviceType) {
            case 'mobile':
                return part[mobileProp] !== undefined ? part[mobileProp] : part[baseProp];
            case 'tablet':
                return part[tabletProp] !== undefined ? part[tabletProp] : part[baseProp];
            default:
                return part[baseProp];
        }
    }

    getCircleSize() {
        const sizes = this.options.defaultCircleSize;
        switch (this.deviceType) {
            case 'mobile': return sizes.mobile;
            case 'tablet': return sizes.tablet;
            default: return sizes.desktop;
        }
    }

    createClickableArea(circleSize) {
        const clickableArea = document.createElement('div');
        clickableArea.className = 'hotspot-clickable-area';
        clickableArea.style.cssText = `
            position: absolute;
            width: ${circleSize}px;
            height: ${circleSize}px;
            left: -${circleSize / 2}px;
            top: -${circleSize / 2}px;
            border-radius: 50%;
            cursor: pointer;
            z-index: 10;
            opacity: 0;
        `;
        return clickableArea;
    }

    createHotspotElement(containerId, index) {
        const hotspot = document.createElement('div');
        hotspot.className = 'hotspot';
        hotspot.id = `${containerId}-hotspot-${index}`;
        hotspot.style.cssText = 'position: absolute; left: -12px; top: -12px;';

        const outerCircle = document.createElement('div');
        outerCircle.className = 'hotspot-outer-circle';
        hotspot.appendChild(outerCircle);

        return hotspot;
    }

    createLabelContainer(part) {
        const labelOffsetX = this.getLabelOffset(part, 'X');
        const labelOffsetY = this.getLabelOffset(part, 'Y');

        const labelContainer = document.createElement('div');
        labelContainer.className = 'label-container z-1';
        labelContainer.style.position = 'absolute';

        if (part.usePercentage) {
            if (labelOffsetX === 0) {
                labelContainer.style.left = '0';
                labelContainer.style.transform = 'translateX(-50%)';
            } else {
                const unit = this.options.useEmUnits ? 'em' : 'vw';
                labelContainer.style.left = `${labelOffsetX}${unit}`;
            }
            const unit = this.options.useEmUnits ? 'em' : 'vw';
            labelContainer.style.top = `${labelOffsetY}${unit}`;
        } else {
            labelContainer.style.left = `${labelOffsetX || 0}px`;
            labelContainer.style.top = `${labelOffsetY || 15}px`;
            if (!labelOffsetX) {
                labelContainer.style.transform = 'translateX(-50%)';
            }
        }

        const label = document.createElement('div');
        label.className = 'label';
        label.innerHTML = part.name;
        labelContainer.appendChild(label);

        // Only add inline description on desktop when NOT using description panel
        // Mobile and tablet use popup instead
        if (!this.options.hasDescriptionPanel && this.isDesktop()) {
            const descriptionContainer = document.createElement('div');
            descriptionContainer.className = 'description-container';

            const descriptionText = document.createElement('p');
            descriptionText.className = 'description-text';
            descriptionText.innerHTML = part.description;
            descriptionContainer.appendChild(descriptionText);

            if (this.options.hasLearnMore && part.learnMoreUrl) {
                const learnMoreLink = document.createElement('a');
                learnMoreLink.className = 'learn-more-link my-1';
                learnMoreLink.href = this.getLearnMoreUrl(part);
                learnMoreLink.textContent = this.__('LEARN MORE');
                descriptionContainer.appendChild(learnMoreLink);
            }

            labelContainer.appendChild(descriptionContainer);
        }

        return labelContainer;
    }

    getLearnMoreUrl(part) {
        const isTurkish = window.location.pathname.split('/')[1] === 'tr';
        return isTurkish ? (part.learnMoreUrlTr || part.learnMoreUrl || '#') : (part.learnMoreUrl || '#');
    }

    createHotspots(containerId, parts) {
        const container = document.getElementById(containerId);
        if (!container) return;

        container.querySelectorAll('.hotspot-container').forEach(el => el.remove());

        const circleSize = this.getCircleSize();

        parts.forEach((part, index) => {
            const hotspotContainerId = `${containerId}-part-${index}`;
            const hotspotContainer = document.createElement('div');
            hotspotContainer.className = `hotspot-container ${hotspotContainerId}`;
            hotspotContainer.style.cssText = `position: absolute; left: ${this.getCoordinates(part, 'X')}%; top: ${this.getCoordinates(part, 'Y')}%;`;

            const clickableArea = this.createClickableArea(circleSize);
            const hotspot = this.createHotspotElement(containerId, index);
            const labelContainer = this.createLabelContainer(part);

            hotspotContainer.appendChild(clickableArea);
            hotspotContainer.appendChild(hotspot);
            hotspotContainer.appendChild(labelContainer);
            container.appendChild(hotspotContainer);

            this.attachHotspotEvents(hotspotContainer, clickableArea, hotspot, labelContainer, part, index, hotspotContainerId);

            if (part.additionalHotspots && part.additionalHotspots.length > 0) {
                this.createAdditionalHotspots(container, part, index, hotspotContainerId, circleSize);
            }
        });
    }

    createAdditionalHotspots(container, part, index, hotspotContainerId, circleSize) {
        part.additionalHotspots.forEach((additionalSpot, spotIndex) => {
            const additionalContainer = document.createElement('div');
            additionalContainer.className = `hotspot-container additional ${hotspotContainerId}`;

            let additionalX, additionalY;
            switch (this.deviceType) {
                case 'mobile':
                    additionalX = additionalSpot.mobileX !== undefined ? additionalSpot.mobileX : additionalSpot.x;
                    additionalY = additionalSpot.mobileY !== undefined ? additionalSpot.mobileY : additionalSpot.y;
                    break;
                case 'tablet':
                    additionalX = additionalSpot.tabletX !== undefined ? additionalSpot.tabletX : additionalSpot.x;
                    additionalY = additionalSpot.tabletY !== undefined ? additionalSpot.tabletY : additionalSpot.y;
                    break;
                default:
                    additionalX = additionalSpot.x;
                    additionalY = additionalSpot.y;
            }

            additionalContainer.style.cssText = `position: absolute; left: ${additionalX}%; top: ${additionalY}%;`;

            const additionalHotspot = document.createElement('div');
            additionalHotspot.className = 'hotspot';
            additionalHotspot.id = `${container.id}-hotspot-${index}-additional-${spotIndex}`;
            additionalHotspot.style.cssText = 'position: absolute; left: -12px; top: -12px;';

            const additionalOuterCircle = document.createElement('div');
            additionalOuterCircle.className = 'hotspot-outer-circle';
            additionalHotspot.appendChild(additionalOuterCircle);

            const additionalClickableArea = this.createClickableArea(circleSize);

            additionalContainer.appendChild(additionalClickableArea);
            additionalContainer.appendChild(additionalHotspot);
            container.appendChild(additionalContainer);

            this.attachAdditionalHotspotEvents(additionalContainer, additionalClickableArea, part, index, hotspotContainerId);
        });
    }

    attachHotspotEvents(hotspotContainer, clickableArea, hotspot, labelContainer, part, index, hotspotContainerId) {
        const self = this;

        const handleTouch = (e) => {
            e.preventDefault();
            if (self.isMobile()) {
                self.showMobileInfo(part, hotspotContainer);
            } else if (self.isTablet()) {
                self.handleTabletTouch(hotspotContainer, part, index);
            } else {
                self.toggleDesktopHotspot(hotspotContainerId);
            }
        };

        const handleClick = (e) => {
            // On tablet, handle click like touch (for devices that don't fire touchstart)
            if (self.isTablet()) {
                e.preventDefault();
                self.handleTabletTouch(hotspotContainer, part, index);
            } else if (self.isDesktop() && self.options.hasLearnMore) {
                e.preventDefault();
                self.navigateToLearnMore(part);
            }
        };

        clickableArea.addEventListener('touchstart', handleTouch);
        clickableArea.addEventListener('click', handleClick);
        hotspotContainer.addEventListener('touchstart', handleTouch);
        hotspotContainer.addEventListener('click', handleClick);
        labelContainer.addEventListener('touchstart', handleTouch);
        labelContainer.addEventListener('click', handleClick);

        hotspotContainer.addEventListener('mouseenter', () => {
            // Only handle mouseenter on desktop (not mobile/tablet)
            if (self.isDesktop()) {
                document.querySelectorAll(`.${hotspotContainerId}`).forEach(el => el.classList.add('active'));
                if (self.options.hasDescriptionPanel) {
                    self.updateDescriptionPanel(index);
                    self.currentHotspotIndex = index;
                }
            }
        });

        hotspotContainer.addEventListener('mouseleave', () => {
            // Only handle mouseleave on desktop (not mobile/tablet)
            if (self.isDesktop()) {
                setTimeout(() => {
                    document.querySelectorAll(`.${hotspotContainerId}`).forEach(el => el.classList.remove('active'));
                }, self.options.hasLearnMore ? 1000 : 0);

                if (self.options.hasDescriptionPanel && self.currentHotspotIndex === index) {
                    self.resetDescriptionPanel();
                    self.currentHotspotIndex = -1;
                }
            }
        });
    }

    attachAdditionalHotspotEvents(additionalContainer, additionalClickableArea, part, index, hotspotContainerId) {
        const self = this;

        const handleTouch = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (self.isMobile()) {
                self.showMobileInfo(part, additionalContainer);
            } else if (self.isTablet()) {
                self.handleTabletTouch(additionalContainer, part, index);
            } else {
                self.toggleDesktopHotspot(hotspotContainerId);
            }
        };

        const handleClick = (e) => {
            // On tablet, handle click like touch (for devices that don't fire touchstart)
            if (self.isTablet()) {
                e.preventDefault();
                self.handleTabletTouch(additionalContainer, part, index);
            } else if (self.isDesktop() && self.options.hasLearnMore) {
                e.preventDefault();
                self.navigateToLearnMore(part);
            }
        };

        additionalClickableArea.addEventListener('touchstart', handleTouch);
        additionalClickableArea.addEventListener('click', handleClick);
        additionalContainer.addEventListener('touchstart', handleTouch);
        additionalContainer.addEventListener('click', handleClick);

        additionalContainer.addEventListener('mouseenter', () => {
            // Only handle mouseenter on desktop (not mobile/tablet)
            if (self.isDesktop()) {
                document.querySelectorAll(`.${hotspotContainerId}`).forEach(el => el.classList.add('active'));
                if (self.options.hasDescriptionPanel) {
                    self.updateDescriptionPanel(index);
                }
            }
        });

        additionalContainer.addEventListener('mouseleave', () => {
            // Only handle mouseleave on desktop (not mobile/tablet)
            if (self.isDesktop()) {
                setTimeout(() => {
                    document.querySelectorAll(`.${hotspotContainerId}`).forEach(el => el.classList.remove('active'));
                }, self.options.hasLearnMore ? 1000 : 0);

                if (self.options.hasDescriptionPanel) {
                    self.resetDescriptionPanel();
                }
            }
        });
    }

    handleTabletTouch(hotspotContainer, part, index) {
        // On tablet, always show mobile info popup for better UX
        this.showMobileInfo(part, hotspotContainer);
    }

    toggleDesktopHotspot(hotspotContainerId) {
        document.querySelectorAll('.hotspot-container').forEach(c => c.classList.remove('active'));
        document.querySelectorAll(`.${hotspotContainerId}`).forEach(el => el.classList.add('active'));
    }

    navigateToLearnMore(part) {
        const targetUrl = this.getLearnMoreUrl(part);
        if (targetUrl && targetUrl !== '#') {
            window.location.href = targetUrl;
        }
    }

    setupMobileInteractions() {
        const mobileInfo = document.querySelector('.mobile-hotspot-info');
        const mobileOverlay = document.querySelector('.mobile-overlay');
        const closeButton = document.querySelector('.mobile-hotspot-close');

        if (!mobileInfo) return;

        const self = this;

        const closeMobileInfo = () => {
            mobileInfo.classList.remove('active');
            if (mobileOverlay) mobileOverlay.classList.remove('active');

            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';

            // Clear active hotspot
            if (self.activeHotspot) {
                self.activeHotspot.classList.remove('active-info');
                self.activeHotspot = null;
            }

            // Clear active container
            if (self.activeHotspotContainer) {
                self.activeHotspotContainer.classList.remove('active');
                self.activeHotspotContainer = null;
            }

            // Clear all related hotspot containers by class
            if (self.activeHotspotContainerClass) {
                document.querySelectorAll(`.${self.activeHotspotContainerClass}`).forEach(container => {
                    container.classList.remove('active');
                    const hotspotDot = container.querySelector('.hotspot');
                    if (hotspotDot) hotspotDot.classList.remove('active-info');
                });
                self.activeHotspotContainerClass = null;
            }

            // Also clear any remaining active hotspots globally
            document.querySelectorAll('.hotspot.active-info').forEach(el => {
                el.classList.remove('active-info');
            });
            document.querySelectorAll('.hotspot-container.active').forEach(el => {
                el.classList.remove('active');
            });
        };

        if (closeButton) closeButton.addEventListener('click', closeMobileInfo);
        if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileInfo);

        mobileInfo.addEventListener('click', (e) => e.stopPropagation());

        if (!this.options.hasOverlay) {
            document.addEventListener('touchstart', (e) => {
                if (mobileInfo.classList.contains('active')) {
                    if (!mobileInfo.contains(e.target) && !e.target.closest('.hotspot-container')) {
                        closeMobileInfo();
                    }
                }
            });
        }
    }

    showMobileInfo(part, sourceHotspot) {
        const mobileInfo = document.querySelector('.mobile-hotspot-info');
        const mobileOverlay = document.querySelector('.mobile-overlay');
        const mobileTitle = document.querySelector('.mobile-hotspot-title');
        const mobileDescription = document.querySelector('.mobile-hotspot-description');
        const mobileLink = document.querySelector('.mobile-hotspot-link');

        if (!mobileInfo || !mobileTitle || !mobileDescription) return;

        document.querySelectorAll('.hotspot.active-info').forEach(el => el.classList.remove('active-info'));

        mobileTitle.innerHTML = part.name;
        mobileDescription.innerHTML = part.description;

        if (mobileLink && this.options.hasLearnMore) {
            mobileLink.href = this.getLearnMoreUrl(part);
            mobileLink.style.display = 'block';
        } else if (mobileLink) {
            mobileLink.style.display = 'none';
        }

        mobileInfo.classList.add('active');
        if (mobileOverlay) mobileOverlay.classList.add('active');

        document.body.style.overflow = 'hidden';

        if (sourceHotspot) {
            let containerIdClass = null;
            sourceHotspot.classList.forEach(className => {
                if (className.includes('-part-')) {
                    containerIdClass = className;
                }
            });

            if (containerIdClass) {
                this.activeHotspotContainerClass = containerIdClass;
                document.querySelectorAll(`.${containerIdClass}`).forEach(container => {
                    container.classList.add('active');
                    const hotspotDot = container.querySelector('.hotspot');
                    if (hotspotDot) hotspotDot.classList.add('active-info');
                });
            }
        }

        const hotspot = sourceHotspot?.querySelector('.hotspot');
        if (hotspot) {
            hotspot.classList.add('active-info');
            this.activeHotspot = hotspot;
        }

        this.activeHotspotContainer = sourceHotspot;
    }

    updateDescriptionPanel(index) {
        if (!this.options.descriptionPanelSelector) return;

        const panel = document.querySelector(this.options.descriptionPanelSelector);
        if (!panel) return;

        const part = this.parts[index];
        if (!part) return;

        panel.classList.add('active');
        const titleEl = panel.querySelector('.description-title');
        const textEl = panel.querySelector('.description-text');

        if (titleEl) titleEl.innerHTML = part.name;
        if (textEl) textEl.innerHTML = part.description;
    }

    resetDescriptionPanel() {
        if (!this.options.descriptionPanelSelector) return;

        const panel = document.querySelector(this.options.descriptionPanelSelector);
        if (!panel) return;

        panel.classList.remove('active');
        const titleEl = panel.querySelector('.description-title');
        const textEl = panel.querySelector('.description-text');

        if (titleEl) titleEl.textContent = this.__('Select a feature');
        if (textEl) textEl.textContent = this.__('Hover over or tap on any point to learn more.');
    }

    handleResize() {
        const newDeviceType = this.getDeviceType();
        if (newDeviceType !== this.deviceType) {
            this.deviceType = newDeviceType;
            return true;
        }
        return false;
    }

    destroy() {
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
    }
}

window.HotspotManager = HotspotManager;
